import axios, { AxiosError } from 'axios';
import i18n from 'i18next';

import { toPreferredLanguage } from '@shared/config';

// Default to the same-origin `/api` path: the landing's CloudFront
// proxies `/api/*` to the API ALB (like the dashboard), so no CORS and
// no per-environment build variable are needed. VITE_API_URL can still
// override it (e.g. pointing at a remote API in local dev).
//
// The API always lives under the `/api` namespace. Guard against a bare-origin
// override (e.g. a stage VITE_API_URL=https://api.stage.done-it.app, which 404s
// on `/v4/...`): ensure the base ends in `/api`. The relative `/api` default and
// an explicit `…/api` value are both kept as-is.
const raw = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');
const baseURL = raw === '' || raw.endsWith('/api') ? raw || '/api' : `${raw}/api`;

/**
 * Single axios instance for all Done-it API traffic.
 *
 * We don't carry auth cookies — the landing talks to the API
 * cross-origin and uses Token auth (issued by verify-email).
 * `withCredentials` stays off; CORS allow-list on the API side
 * is keyed on the landing origin (LANDING_ORIGIN env, T3).
 */
export const api = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { Accept: 'application/json' },
});

/**
 * Tell the API which language to answer in.
 *
 * Django translates DRF's error messages, and on `/api/` paths it takes the
 * language from `Accept-Language` alone — `APIAcceptLanguageMiddleware`, added
 * by DIT-345 so a stale domain-wide `django_language` cookie could not outrank
 * a client's own choice. The dashboard has sent this header since; the funnel
 * never did, so every server message it showed was in the language of the
 * *browser* rather than the language of the page. DIT-478's recording is the
 * evidence: a throttle error in Spanish on a Dutch sign-up form.
 *
 * The value is the product language, not the URL locale, for the same reason
 * the payload carries one: `LANGUAGES` has no plain `nl`, so Django resolves
 * `nl` through `get_supported_language_variant` to the first supported `nl-*`
 * — `nl-nl`, Netherlands Dutch — and hands a Flemish reader the wrong variant.
 * `nl-be` asks for the catalogue that actually exists.
 *
 * An interceptor rather than a default header: the funnel's language can change
 * after this module is evaluated (the market switcher, and any marketing → funnel
 * CTA), so it has to be read per request.
 */
api.interceptors.request.use((config) => {
  config.headers.set('Accept-Language', toPreferredLanguage(i18n.resolvedLanguage));
  return config;
});

/**
 * Normalised representation of a DRF error body, suited to react-
 * hook-form's setError(field, …).
 * - field-level errors: { admin_email: 'already exists', vat: '…' }
 * - top-level non-field error: { _root: 'message' }.
 */
export interface NormalisedApiError {
  status: number;
  fields: Record<string, string>;
  message?: string;
  /**
   * Seconds until the caller may retry, from the `Retry-After` header DRF sets
   * on a 429. Present so the caller can word the wait itself: DRF's own copy
   * for this case is poor in every language we ship — its Dutch for "Request
   * was throttled." is "Aanvraag was verstikt.", and the sentence carrying the
   * number is untranslated upstream, so even a correctly localised response
   * comes back half in English. This is the one message a stuck customer
   * actually reads (DIT-478), so the funnel writes its own.
   */
  retryAfterSeconds?: number;
}

export function isAxiosError(e: unknown): e is AxiosError {
  return axios.isAxiosError(e);
}

/**
 * `Retry-After` as whole minutes, floored at one — the unit to say a wait in.
 *
 * DRF reports it in seconds, and the registration throttle is an hour-long
 * bucket, so the raw value is the likes of "997 seconds". Never zero: a wait
 * the customer is told is over, but that the server still refuses, is worse
 * than rounding up.
 */
export function retryAfterMinutes(seconds?: number): number {
  return Math.max(1, Math.ceil((seconds ?? 0) / 60));
}

export function normaliseDrfError(error: unknown): NormalisedApiError {
  if (!isAxiosError(error)) {
    return {
      status: 0,
      fields: {},
      message: i18n.t('errors.unexpected', { ns: 'common' }),
    };
  }
  const status = error.response?.status ?? 0;
  const retryAfter = Number(error.response?.headers?.['retry-after']);
  const retryAfterSeconds = Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined;
  const data = error.response?.data as Record<string, unknown> | string | undefined;
  if (typeof data === 'string') return { status, fields: {}, message: data, retryAfterSeconds };
  if (!data) return { status, fields: {}, message: error.message, retryAfterSeconds };
  const fields: Record<string, string> = {};
  let topLevel: string | undefined;
  for (const [key, value] of Object.entries(data)) {
    const flat = Array.isArray(value) ? String(value[0]) : String(value);
    if (key === 'detail' || key === 'error' || key === 'non_field_errors') {
      topLevel = flat;
    } else {
      fields[key] = flat;
    }
  }
  return { status, fields, message: topLevel, retryAfterSeconds };
}
